import { Component, Input, OnChanges } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { TranslateService } from '@ngx-translate/core';
import { BenchForecastData } from '../../../interfaces/metrics';

// ISO week number, computed at UTC noon with UTC getters (same algorithm as
// dashboard.component.ts's weekNo and company-util-trend's isoWeekNo).
function isoWeekNo(iso: string): number {
  const d = new Date(iso + 'T12:00:00Z');
  d.setUTCDate(d.getUTCDate() + 3 - ((d.getUTCDay() + 6) % 7));
  const week1 = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((d.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getUTCDay() + 6) % 7)) / 7);
}

@Component({
  selector: 'bst-company-bench-forecast',
  templateUrl: './company-bench-forecast.component.html',
  styleUrls: ['./company-bench-forecast.component.scss'],
  standalone: false
})
export class CompanyBenchForecastComponent implements OnChanges {
  @Input() data!: BenchForecastData;
  options: EChartsOption = {};
  // Spec §7: fewer than 2 weeks of forecast reads as one lone bar in an empty frame,
  // not a forecast. CompanyBenchForecastResolver always emits exactly horizonWeeks
  // (4-13) rows, so this only trips on a malformed/short payload, never on a real one:
  // it means "no forecast history at all", not "no headcount variation in it" (see
  // allZero below, and capacity-forecast/company-util-trend for the same split).
  // Already a stacked-column chart, never a smooth area, so item 1 of the Tier A brief
  // is otherwise satisfied as-is.
  sparse = false;
  // Every week has zero booked+bench headcount: a real reading (every member's active
  // membership window falls outside the horizon), not missing data, so it gets its own
  // true message instead of "not enough data". A week with SOME headcount but zero
  // bench is not this case (it is a real, fully-booked week and renders normally); only
  // filtering by nonzero week TOTAL discarded a departing/joining member's genuinely
  // empty weeks as if they were absent, which is the bug this replaced.
  allZero = false;

  constructor(private translate: TranslateService) {}

  ngOnChanges(): void {
    if (!this.data || !this.data.points?.length) { return; }

    const points = this.data.points;
    // Both assigned before either return (mirrors company-util-trend): leaving allZero
    // unset on the sparse branch let it go stale at whatever it last was.
    this.sparse = points.length < 2;
    this.allZero = !this.sparse && points.every(p => (p.booked + p.bench) === 0);
    if (this.sparse || this.allZero) { return; }
    const maxTotal = Math.max(...points.map(p => p.booked + p.bench));
    const bookedLabel = this.translate.instant('dashboard.company.bench_forecast_booked');
    const benchLabel = this.translate.instant('dashboard.company.bench_forecast_bench');
    const benchHoursLabel = this.translate.instant('dashboard.company.bench_forecast_bench_hours');

    this.options = {
      grid: { left: 30, right: 12, top: 14, bottom: 24 },
      xAxis: {
        type: 'category',
        data: points.map(p => 'w' + isoWeekNo(p.weekStart)),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontFamily: 'inherit', fontWeight: 600 }
      },
      yAxis: {
        type: 'value', min: 0, max: Math.ceil(maxTotal), minInterval: 1,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: { color: '#6B7A89', fontFamily: 'inherit', fontWeight: 600 },
        splitLine: { lineStyle: { color: '#EEF1F5' } }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        backgroundColor: '#fff',
        borderColor: '#EEF1F5',
        textStyle: { color: '#102C45', fontFamily: 'inherit' },
        formatter: (params: any) => {
          const p = points[params[0].dataIndex];
          return `<div style="font-weight:700;margin-bottom:4px">${params[0].axisValueLabel}</div>`
            + `<div>${bookedLabel}: ${p.booked}</div>`
            + `<div>${benchLabel}: ${p.bench}</div>`
            + `<div>${benchHoursLabel}: ${p.benchHours}</div>`;
        }
      },
      series: [
        {
          type: 'bar',
          stack: 'w',
          name: bookedLabel,
          data: points.map(p => p.booked),
          itemStyle: { color: '#1863DC', borderRadius: [0, 0, 2, 2] },
          barWidth: 22
        },
        {
          type: 'bar',
          stack: 'w',
          name: benchLabel,
          data: points.map(p => p.bench),
          itemStyle: { color: '#CBD6E2', borderRadius: [2, 2, 0, 0] }
        }
      ],
      animation: false
    };
  }
}
